import { twMerge } from "tailwind-merge";
import { Icon } from "@/components/Icon";
import { sizeStyles } from "../constants";
import type { HierarchyNode } from "../types";

interface StickyRootNodeProps<T extends HierarchyNode> {
  node: T;
  position: "top" | "bottom";
  selectedId: string | null;
  variant: "subtle" | "solid";
  size: "xs" | "sm" | "md";
  showBullets: boolean;
}

export function StickyRootNode<T extends HierarchyNode>({
  node,
  position,
  selectedId,
  variant,
  size,
  showBullets,
}: StickyRootNodeProps<T>) {
  const isSelected = selectedId === node.id;

  return (
    <div
      className={twMerge(
        "absolute z-30 left-0 right-0 border-border-default transition-all duration-200 bg-background-surface",
        sizeStyles[size].node,
        isSelected
          ? variant === "subtle"
            ? "text-text-link-default"
            : "bg-action-brand text-[color:var(--color-action-brand-text)]"
          : "font-semibold",
        position === "top" && "top-0 border-b shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]",
        position === "bottom" && "bottom-0 border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
      )}
    >
      {/* Subtle selection overlay for subtle variant when selected */}
      {isSelected && variant === "subtle" && <div className="absolute inset-0 bg-background-selected" />}
      <div className="flex items-center gap-2 cursor-pointer relative z-10">
        {showBullets && (
          <div
            className="flex-shrink-0 rounded-full flex items-center justify-center bg-action-brand border-action-brand"
            style={{
              width: sizeStyles[size].bullet,
              height: sizeStyles[size].bullet,
            }}
          >
            {node.icon && (
              <Icon
                name={node.icon as Parameters<typeof Icon>[0]["name"]}
                size={sizeStyles[size].icon}
                weight="fill"
                className="text-[color:var(--color-action-brand-text)]"
              />
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <span className="truncate">{node.name}</span>
        </div>
      </div>
    </div>
  );
}
